// Homepage — hero + value props + CTA.
// Redesigned as a modern landing page with a dark hero, product pitch,
// feature grid, and primary CTA.

import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import AppHeader from './components/AppHeader';
import DropsWidget from './components/DropsWidget';
import { theme, colors } from './theme';

const FEATURE_KEYS = [
  { key: 'vision', icon: <VideocamOutlinedIcon sx={{ fontSize: 32, color: colors.brand }} /> },
  { key: 'segments', icon: <BarChartIcon sx={{ fontSize: 32, color: colors.brand }} /> },
  { key: 'sensors', icon: <BoltIcon sx={{ fontSize: 32, color: colors.brand }} /> },
  { key: 'ai', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 32, color: colors.brand }} /> },
  { key: 'equipment', icon: <DirectionsBikeIcon sx={{ fontSize: 32, color: colors.brand }} /> },
  { key: 'refinement', icon: <AutoAwesomeIcon sx={{ fontSize: 32, color: colors.brand }} /> },
];

export const Homepage = (_props: { theme?: any }) => {
  const { t } = useTranslation('homepage');
  // Wave 15 — homepage-visit ping. Server rate-limits by IP hash so
  // this doesn't spam Slack on refresh loops.
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API || '';
    if (!apiBase) return;
    fetch(`${apiBase}/events/homepage-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        host: window.location.host,
        path: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundImage: colors.gradHero }}>
        <AppHeader showNav={false} />
        <DropsWidget />

        {/* Hero */}
        <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 14 }, pb: { xs: 10, md: 16 } }}>
          <Stack spacing={5} alignItems="center" textAlign="center">
            {t('badge') && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  backgroundColor: 'rgba(106, 255, 69, 0.08)',
                  border: `1px solid ${colors.borderFocus}`,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: colors.brand,
                    boxShadow: `0 0 12px ${colors.brand}`,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: colors.brand,
                  }}
                >
                  {t('badge')}
                </Typography>
              </Box>
            )}

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3rem', sm: '4.5rem', md: '6rem' },
                fontWeight: 800,
                letterSpacing: '-0.04em',
                lineHeight: 0.95,
                maxWidth: 900,
              }}
            >
              {t('heroTitle1')}{' '}
              <Box
                component="span"
                sx={{
                  background: colors.gradBrand,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('heroTitle2')}
              </Box>
              {t('heroTitle3') && (
                <>
                  <br />
                  {t('heroTitle3')}
                </>
              )}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: colors.textMuted,
                fontWeight: 400,
                maxWidth: 680,
                lineHeight: 1.5,
              }}
            >
              {t('heroSubtitle')}
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ pt: 2 }}
            >
              <Button
                href="/register"
                variant="contained"
                size="large"
                sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
              >
                {t('ctaPrimary')}
              </Button>
              <Button
                href="/login"
                variant="outlined"
                size="large"
                sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
              >
                {t('ctaSecondary')}
              </Button>
            </Stack>

            {t('privacyNote') && (
              <Typography variant="caption" sx={{ color: colors.textFaint, pt: 2 }}>
                {t('privacyNote')}
              </Typography>
            )}
          </Stack>
        </Container>

        {/* Feature grid */}
        <Box sx={{ borderTop: `1px solid ${colors.border}`, py: { xs: 10, md: 14 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="overline"
              sx={{ display: 'block', textAlign: 'center', color: colors.brand, mb: 2 }}
            >
              {t('featuresOverline')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                mb: 8,
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              {t('featuresTitle')}
            </Typography>

            <Grid container spacing={3}>
              {FEATURE_KEYS.map((f) => (
                <Grid item xs={12} sm={6} md={4} key={f.key}>
                  <Box
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 3,
                      backgroundColor: colors.surface,
                      border: `1px solid ${colors.border}`,
                      transition: 'all 220ms',
                      '&:hover': {
                        borderColor: colors.borderFocus,
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: 'rgba(106, 255, 69, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                      {t(`features.${f.key}.title`)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textMuted, lineHeight: 1.65 }}>
                      {t(`features.${f.key}.body`)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA band */}
        <Box
          sx={{
            py: { xs: 10, md: 14 },
            borderTop: `1px solid ${colors.border}`,
            background: colors.gradHero,
          }}
        >
          <Container maxWidth="md">
            <Stack alignItems="center" spacing={4} textAlign="center">
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800 }}>
                {t('ctaBandTitle')}
              </Typography>
              {t('ctaBandSubtitle') && (
                <Typography variant="body1" sx={{ color: colors.textMuted, maxWidth: 520 }}>
                  {t('ctaBandSubtitle')}
                </Typography>
              )}
              <Button
                href="/register"
                variant="contained"
                size="large"
                sx={{ px: 5, py: 2, fontSize: '1.0625rem' }}
              >
                {t('ctaBandButton')}
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: `1px solid ${colors.border}`, py: 5 }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Typography variant="caption" sx={{ color: colors.textFaint }}>
                {t('footer.copyright', { year: new Date().getFullYear(), ns: 'common' })}
              </Typography>
              <Stack direction="row" spacing={3}>
                <Typography component="a" href="/about" variant="caption" sx={{ color: colors.textMuted, textDecoration: 'none' }}>
                  {t('footerAbout')}
                </Typography>
                <Typography component="a" href="/privacy" variant="caption" sx={{ color: colors.textMuted, textDecoration: 'none' }}>
                  {t('footer.privacy', { ns: 'common' })}
                </Typography>
                <Typography component="a" href="/terms" variant="caption" sx={{ color: colors.textMuted, textDecoration: 'none' }}>
                  {t('footer.terms', { ns: 'common' })}
                </Typography>
                <Typography component="a" href="/liability" variant="caption" sx={{ color: colors.textMuted, textDecoration: 'none' }}>
                  {t('footer.liability', { ns: 'common' })}
                </Typography>
                <Typography component="a" href="/login" variant="caption" sx={{ color: colors.textMuted, textDecoration: 'none' }}>
                  {t('footerSignIn')}
                </Typography>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
